import { API_BASE_URL, getAuthHeaders } from "./api";

export const adminAPI = {
  // Dashboard stats
  async getDashboardStats() {
    const response = await fetch(`${API_BASE_URL}/admin/stats`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Error fetching admin dashboard stats");
    }

    return response.json();
  },

  // Categories management
  async getCategories() {
    const response = await fetch(`${API_BASE_URL}/categories`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Error fetching categories");
    }

    return response.json();
  },

  async getStates() {
    const response = await fetch(`${API_BASE_URL}/states`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Error fetching categories");
    }

    return response.json();
  },

  async createCategory(categoryData) {
    const response = await fetch(`${API_BASE_URL}/admin/categories`, {
      method: "POST",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(categoryData),
    });

    if (!response.ok) {
      throw new Error("Error creating category");
    }

    return response.json();
  },

  async updateCategory(categoryId, updateData) {
    const response = await fetch(`${API_BASE_URL}/admin/categories/${categoryId}`, {
      method: "PUT",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      throw new Error("Error updating category");
    }

    return response.json();
  },

  async deleteCategory(categoryId) {
    const response = await fetch(`${API_BASE_URL}/admin/categories/${categoryId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Error deleting category");
    }

    return response.json();
  },

  async reorderCategories(orderData) {
    const response = await fetch(`${API_BASE_URL}/admin/categories/reorder`, {
      method: "PUT",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(orderData),
    });

    if (!response.ok) {
      throw new Error("Error reordering categories");
    }

    return response.json();
  },

  // Tramites management
  async getTramites() {
    const response = await fetch(`${API_BASE_URL}/admin/type`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Error fetching tramites");
    }

    return response.json();
  },

  async createTramite(tramiteData) {
    const response = await fetch(`${API_BASE_URL}/admin/type`, {
      method: "POST",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(tramiteData),
    });

    if (!response.ok) {
      throw new Error("Error creating tramite");
    }

    return response.json();
  },

  async updateTramite(tramiteId, updateData) {
    const response = await fetch(`${API_BASE_URL}/admin/type/${tramiteId}`, {
      method: "PUT",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      throw new Error("Error updating tramite");
    }

    return response.json();
  },

  async deleteTramite(tramiteId) {
    const response = await fetch(`${API_BASE_URL}/admin/type/${tramiteId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Error deleting tramite");
    }

    return response.json();
  },

  // Form templates management
  async getFormTemplate(tramiteId) {
    const response = await fetch(`${API_BASE_URL}/applications/admin/${tramiteId}/admin/form`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Error fetching form template");
    }

    return response.json();
  },

  async updateFormTemplate(tramiteId, formFields) {
    const response = await fetch(`${API_BASE_URL}/admin/type/${tramiteId}/admin/form`, {
      method: "PUT",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formFields),
    });

    if (!response.ok) {
      throw new Error("Error updating form template");
    }

    return response.json();
  },

  // Form templates management
  async getFormTemplates() {
    const response = await fetch(`${API_BASE_URL}/admin/forms`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Error fetching form templates");
    }

    return response.json();
  },

  async createFormTemplate(templateData) {
    const response = await fetch(`${API_BASE_URL}/admin/forms`, {
      method: "POST",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(templateData),
    });

    if (!response.ok) {
      throw new Error("Error creating form template");
    }

    return response.json();
  },

  async updateFormTemplate(templateId, updateData) {
    const response = await fetch(`${API_BASE_URL}/admin/forms/${templateId}`, {
      method: "PUT",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      throw new Error("Error updating form template");
    }

    return response.json();
  },

  async deleteFormTemplate(templateId) {
    const response = await fetch(`${API_BASE_URL}/admin/forms/${templateId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Error deleting form template");
    }

    return response.json();
  },

  async toggleFormTemplatePublish(templateId, publish) {
    const response = await fetch(`${API_BASE_URL}/admin/forms/${templateId}/publish?publish=${publish}`, {
      method: "PUT",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Error toggling form template publish status");
    }

    return response.json();
  },

  async duplicateFormTemplate(templateId) {
    const response = await fetch(`${API_BASE_URL}/admin/forms/${templateId}/duplicate`, {
      method: "POST",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Error duplicating form template");
    }

    return response.json();
  },

  // Documents management
  async getPendingDocuments() {
    const response = await fetch(`${API_BASE_URL}/managers/pending-documents`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Error fetching pending documents");
    }

    return response.json();
  },

  async validateDocument(documentId, validation) {
    const response = await fetch(`${API_BASE_URL}/managers/documents/${documentId}/review`, {
      method: "PUT",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(validation),
    });

    if (!response.ok) {
      throw new Error("Error validating document");
    }

    return response.json();
  },
};
